"use client";

import { useState } from "react";
import Image from "next/image";
import { IpoCard } from "@/app/components/icm 3.0/shared/IpoCard";
import { ProductCard } from "@/app/components/icm 3.0/shared/ProductCard";
import { HomepageAssets } from "@/app/components/icm 3.0/homepage-assets";
import ChevronDown from "@/app/assets/icons/chevron-down";
import { DirectPaths } from "@/config/routes";
import { cn } from "@/lib/utils";

/**
 * Explore InCred Money product grid
 * Desktop: Figma 5882:65739
 * Mobile:  Figma 6679:10751
 */
export function ProductGrid() {
  const [showMore, setShowMore] = useState(false);

  return (
    <div className="flex w-full flex-col items-center gap-6 md:gap-5">
      <header className="flex w-full flex-col items-center gap-1 text-center md:gap-2">
        <p className="w-full text-center text-base font-semibold uppercase leading-[22px] tracking-[2px] text-brand-primary-500 md:text-xl md:leading-[26px]">
          Explore InCred Money
        </p>
        <h2 className="text-center text-[length:var(--typography-font-size-2xl)] font-bold leading-8 tracking-[0] text-black md:text-5xl md:leading-[58px]">
          Grow your money, your way.
        </h2>
      </header>

      {/* Mobile: stacked cards. Desktop: fluid mosaic (fills max-w-[1200px], no fixed px that overflow mid viewports). */}
      <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2 md:gap-5">
        <ProductCard
          badge="BROKING"
          badgeVariant="orange"
          title="Stocks"
          description="Trade stocks with zero brokerage. Fast execution and powerful tools, built for active investors."
          ctaText="Start Trading"
          ctaHref={DirectPaths.STOCKS}
          analyticsEvent="Home_Explore_Indian_Stocks"
          newLabel="New!"
          textSize="text-[length:var(--typography-font-size-2xl)] leading-8 md:text-5xl md:leading-[58px]"
          className="h-[286px] w-full min-w-0 bg-[linear-gradient(159.34deg,#FCEAE0_1.03%,#FFF_83.87%)] md:h-[512px] md:bg-[linear-gradient(157.72deg,#FCEAE0_1.03%,#FFF_83.87%)]"
          contentClassName="max-w-[309px] md:max-w-[340px]"
          descriptionClassName="max-w-[271px] md:max-w-none md:text-lg md:leading-6"
          illustrationClassName="absolute left-[calc(50%+108px)] top-[calc(50%+81.5px)] size-[133px] -translate-x-1/2 -translate-y-1/2 md:left-[calc(50%+142px)] md:top-[calc(50%+113.5px)] md:size-[285px]"
        >
          <Image
            src={HomepageAssets.stocks}
            alt=""
            width={285}
            height={285}
            className="size-full object-contain"
            unoptimized
            aria-hidden
          />
        </ProductCard>

        <ProductCard
          badge="INVEST INTERNATIONALLY"
          badgeVariant="green"
          title="Global Stocks"
          description="Access global investing and build a portfolio beyond India. Discover new markets and opportunities worldwide."
          ctaText="Explore global stocks"
          ctaHref={`${DirectPaths.GLOBAL}${DirectPaths.US_STOCKS}`}
          analyticsEvent="Home_Nav_Bar_US_Stocks"
          textSize="text-[length:var(--typography-font-size-2xl)] leading-8 md:text-5xl md:leading-[58px]"
          className="h-[270px] w-full min-w-0 bg-[linear-gradient(162deg,#F0FDF4_1.03%,#FFF_83.87%)] md:h-[512px] md:bg-[linear-gradient(157.38deg,#F0FDF4_1.03%,#FFF_83.87%)]"
          contentClassName="max-w-[308px] md:max-w-[422px]"
          descriptionClassName="max-w-[236px] md:max-w-[362px] md:text-lg md:leading-6"
          illustrationClassName="absolute left-[calc(50%+121px)] top-[calc(50%+66.5px)] size-[101px] -translate-x-1/2 -translate-y-1/2 md:left-[calc(50%+168px)] md:top-[calc(50%+143.5px)] md:size-[225px]"
        >
          <Image
            src={HomepageAssets.globalStocks}
            alt=""
            width={225}
            height={225}
            className="size-full object-contain"
            unoptimized
            aria-hidden
          />
        </ProductCard>
      </div>

      <div
        className={cn(
          "w-full min-w-0 flex-col gap-6 md:gap-5",
          showMore ? "flex" : "hidden md:flex"
        )}
      >
        <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-[minmax(0,21fr)_minmax(0,10fr)] md:gap-5">
          <IpoCard className="w-full min-w-0 md:h-[564px]" />

          <ProductCard
            badge="DIGITAL GOLD & SILVER"
            badgeVariant="yellow"
            title={
              <>
                Start with{" "}
                <br className="hidden md:block" />
                just ₹10.
              </>
            }
            description="Buy, save, and grow your wealth with 99.9% pure digital gold & silver."
            attribution="Powered by MMTC - PAMP"
            ctaText="Start SIP"
            ctaHref={`${DirectPaths.BULLION_POSTFIX_PATH}${DirectPaths.GOLD}`}
            analyticsEvent="Home_Explore_on_Digital_Gold&_Silver"
            textSize="text-[length:var(--typography-font-size-2xl)] leading-8 md:text-4xl md:leading-[44px]"
            className="h-[258px] w-full min-w-0 bg-[linear-gradient(161.21deg,#FEFCE8_1.03%,#FFF_83.87%)] md:h-[564px] md:bg-[linear-gradient(144.76deg,#FEFCE8_1.03%,#FFF_83.87%)]"
            contentClassName="max-w-[308px] gap-6 md:max-w-[235px]"
            descriptionClassName="max-w-[242px] md:max-w-none md:text-lg md:leading-6"
            illustrationClassName="absolute left-[calc(50%+121.5px)] top-[calc(50%+70px)] size-[118px] -translate-x-1/2 -translate-y-1/2 md:left-[calc(50%+72px)] md:top-[calc(50%+187px)] md:size-[236px]"
          >
            <Image
              src={HomepageAssets.digitalGold}
              alt=""
              width={236}
              height={236}
              className="size-full object-contain"
              unoptimized
              aria-hidden
            />
          </ProductCard>
        </div>

        <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2 md:gap-5">
          <ProductCard
            badge="FUTURES & OPTIONS"
            badgeVariant="orange"
            badgeClassName="border-[#FDE7D7] bg-[#FAFAFA] text-[#E13C15]"
            title="Trade F&O with precision."
            description="Access F&O trading with advanced tools for speed and strategy."
            ctaText="Trade F&O"
            ctaHref={DirectPaths.STOCKS}
            analyticsEvent="Home_Nav_Bar_Broking"
            ctaAtBottom
            textSize="text-[length:var(--typography-font-size-2xl)] leading-8 md:text-[30px] md:leading-9"
            className="h-[246px] w-full min-w-0 bg-[linear-gradient(162deg,#FEF2F2_1.03%,#FFF_83.87%)] md:h-auto md:bg-[linear-gradient(169.09deg,#FEF2F2_1.03%,#FFF_83.87%)]"
            contentClassName="max-w-[308px] md:max-w-none"
            descriptionClassName="max-w-[236px] md:max-w-[287px] md:text-base md:leading-[22px]"
            illustrationClassName="absolute left-[calc(50%+133.5px)] top-[calc(50%+69px)] h-[106px] w-[82px] -translate-x-1/2 -translate-y-1/2 md:left-[auto] md:right-6 md:top-[65px] md:h-[158px] md:w-[121px] md:translate-x-0 md:translate-y-0"
          >
            <Image
              src={HomepageAssets.fno}
              alt=""
              width={121}
              height={158}
              className="size-full object-contain"
              unoptimized
              aria-hidden
            />
          </ProductCard>

          <ProductCard
            badge="FIXED DEPOSITS"
            badgeVariant="blue"
            title="Secure high-yield bank FDs"
            description={
              <>
                Compare 200+ Fds from AAA-rated banks & NBFCs.
                <br className="hidden md:block" />
                Find the right balance of safety, flexibility, and returns.
              </>
            }
            ctaText="Compare Rates"
            ctaHref={DirectPaths.FD_POSTFIX_PATH}
            analyticsEvent="Home_Explore_Fixed_Deposit"
            textSize="text-[length:var(--typography-font-size-2xl)] leading-8 md:text-[30px] md:leading-9"
            className="h-[299px] w-full min-w-0 bg-[linear-gradient(158.48deg,#DEE8FB_1.03%,#FFF_83.87%)] md:h-auto md:bg-[linear-gradient(169.17deg,#DEE8FB_1.03%,#FFF_83.87%)]"
            contentClassName="max-w-[308px] md:max-w-[403px]"
            descriptionClassName="max-w-[260px] md:max-w-none md:text-base md:leading-[22px]"
            illustrationClassName="absolute left-[calc(50%+127px)] top-[calc(50%+97px)] size-[120px] -translate-x-1/2 -translate-y-1/2 md:left-[calc(50%+221.5px)] md:top-[calc(50%+43px)] md:size-[167px]"
          >
            <Image
              src={HomepageAssets.fd}
              alt=""
              width={167}
              height={167}
              className="size-full object-cover"
              unoptimized
              aria-hidden
            />
          </ProductCard>
        </div>
      </div>

      <div className="flex justify-center md:hidden">
        <button
          type="button"
          aria-expanded={showMore}
          onClick={() => setShowMore((open) => !open)}
          className="inline-flex h-[26px] items-center gap-1 text-sm font-medium leading-5 text-cta-ghost-text underline underline-offset-[3px]"
        >
          {showMore ? "View Less" : "View More"}
          <ChevronDown
            className={cn(
              "size-4 transition-transform",
              showMore && "rotate-180"
            )}
          />
        </button>
      </div>
    </div>
  );
}
